import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  User, 
  MessageCircle, 
  FileText, 
  Bot, 
  Calendar, 
  AlertCircle, 
  RefreshCw, 
  Clock 
} from 'lucide-react';
import { useApp } from '../context/useApp';
import { dashboardService } from '../services/dashboardService';

const RealTimeActivities = () => {
  const { user, language, recentActivity } = useApp();
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const translations = {
    english: {
      recentActivity: 'Recent Activity',
      noActivity: 'No recent activity',
      refresh: 'Refresh',
      viewAll: 'View all activities',
      minutesAgo: 'minutes ago',
      hoursAgo: 'hours ago',
      daysAgo: 'days ago',
      justNow: 'Just now'
    },
    hindi: {
      recentActivity: 'हाल की गतिविधि',
      noActivity: 'कोई हाल की गतिविधि नहीं',
      refresh: 'रिफ्रेश',
      viewAll: 'सभी गतिविधियां देखें',
      minutesAgo: 'मिनट पहले',
      hoursAgo: 'घंटे पहले',
      daysAgo: 'दिन पहले',
      justNow: 'अभी-अभी'
    }
  };

  const t = translations[language] || translations.english;

  useEffect(() => {
    if (user?.uid) {
      loadActivities();
      setupRealtimeUpdates();
    }
  }, [user?.uid]);

  const loadActivities = async () => {
    try {
      setIsLoading(true);
      const result = await dashboardService.getRecentActivities(user.uid, 10);
      if (result.success) {
        setActivities(result.data);
      }
    } catch (error) {
      console.error('Error loading activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const setupRealtimeUpdates = () => {
    if (!user?.uid) return;

    const unsubscribe = dashboardService.subscribeToDashboardUpdates(user.uid, (update) => {
      if (update.type === 'activities_update') {
        setActivities(update.activities);
      }
    });

    return unsubscribe;
  };

  const getActivityIcon = (activity) => {
    switch (activity.icon) {
      case 'user':
        return <User className="w-4 h-4 text-blue-600" />;
      case 'message':
        return <MessageCircle className="w-4 h-4 text-blue-600" />;
      case 'document':
        return <FileText className="w-4 h-4 text-green-600" />;
      case 'ai':
        return <Bot className="w-4 h-4 text-purple-600" />;
      case 'booking':
        return <Calendar className="w-4 h-4 text-orange-600" />;
      case 'alert':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case 'refresh':
        return <RefreshCw className="w-4 h-4 text-blue-600" />;
      case 'chat':
        return <MessageCircle className="w-4 h-4 text-green-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return t.justNow;
    
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return t.justNow;
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} ${t.minutesAgo}`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours} ${t.hoursAgo}`;
    } else {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days} ${t.daysAgo}`;
    }
  };

  const handleActivityClick = (activity) => {
    console.log('Activity clicked:', activity);
    
    // Navigate based on activity type
    switch (activity.type) {
      case 'booking':
        if (activity.data?.id) {
          window.location.href = '/my-bookings';
        }
        break;
      case 'chat':
        if (activity.data?.id) {
          window.location.href = `/chat-conversation/${activity.data.id}`;
        }
        break;
      case 'ai':
        window.location.href = '/ai-assistant';
        break;
      case 'document':
        // Navigate to documents section when implemented
        console.log('Document activity clicked');
        break;
      default:
        console.log('Unknown activity type:', activity.type);
    }
  };

  const handleRefresh = async () => {
    await loadActivities();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">{t.recentActivity}</h2>
          <button
            onClick={handleRefresh}
            className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
          >
            <RefreshCw className="w-4 h-4" />
            {t.refresh}
          </button>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="space-y-4">
            {[...Array(5)].map((_, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg animate-pulse">
                <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-lg"></div>
                <div className="flex-1 space-y-2">
                  <div className="w-32 h-4 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  <div className="w-48 h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                  <div className="w-20 h-3 bg-gray-200 dark:bg-gray-600 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">{t.recentActivity}</h2>
        <button
          onClick={handleRefresh}
          className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          {t.refresh}
        </button>
      </div>
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        {activities.length > 0 ? (
          <div className="space-y-4">
            {activities.slice(0, 8).map((activity, index) => (
              <motion.div
                key={`activity-${activity.id || index}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                onClick={() => handleActivityClick(activity)}
              >
                <div className="p-2 bg-white dark:bg-gray-600 rounded-lg shadow-sm">
                  {getActivityIcon(activity)}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{activity.title}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{activity.description}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                </div>
                {activity.priority === 'high' && (
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                )}
              </motion.div>
            ))}
            {activities.length > 8 && (
              <div className="text-center pt-2">
                <button
                  onClick={() => window.location.href = '/activity-log'}
                  className="text-sm text-purple-600 hover:text-purple-700"
                >
                  {t.viewAll} ({activities.length})
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="w-8 h-8 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 mb-2">{t.noActivity}</p>
            <p className="text-xs text-gray-400">Start using the app to see your activities here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealTimeActivities;
